import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'

import { Paper } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import AnaliseScope from '../analise.scopes'
import { AnaliseRow } from '../analise.types'

const LOG = Logger.get('ANALISE-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type AnaliseViewProps = {
    className?: string
    scope: AnaliseScope
}
class AnaliseViewClass implements FCClassContext<AnaliseViewProps> {
    // :: emissors
    readonly emitOnFind = () => {
        this.scope.nomeAnaliseInputRef = this.nomeAnaliseInputRef.current?.value || ''
        this.scope.onFind().catch(LOG.caught)
    }

    readonly emitOnNew = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly handleEditClick = (id: GridRowId) => () => {
        this.scope.idSelected = id as number
        this.scope.onEdit().catch(LOG.caught)
    }

    readonly handleDeleteClick = (id: GridRowId) => () => {
        this.scope.idSelected = id as number
        this.scope.onDelete().catch(LOG.caught)
    }

    // :: Fields
    scope!: AnaliseScope
    sytles!: ReturnType<typeof useStyles>
    nomeAnaliseInputRef!: React.RefObject<HTMLInputElement | null>
    analiseColumns = static_build_analise_columns(this.handleEditClick, this.handleDeleteClick)

    // :: Methods

    onSyncState({ scope }: AnaliseViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: AnaliseViewProps) {
        this.scope = bindUpdate(React, scope)
        this.nomeAnaliseInputRef = React.useRef<HTMLInputElement>(null)

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'baseline',
                        gap: '10px'
                    }}
                >
                    <TextField
                        className={classes.cnpjInput}
                        inputRef={this.nomeAnaliseInputRef}
                        label="Filtre pelo nome da análise"
                        variant="standard"
                    />
                    <Button variant="outlined" onClick={this.emitOnFind}>
                        Buscar
                    </Button>
                    <Button variant="outlined" onClick={this.emitOnNew}>
                        Nova
                    </Button>
                </Box>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <DataGrid
                        rows={this.scope.analises}
                        columns={this.analiseColumns}
                        getRowId={static_getAnaliseRowId}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10
                                }
                            }
                        }}
                    />
                </Paper>
            </div>
        )
    }
}

export const AnaliseView = classToFComponent(AnaliseViewClass, React)
export default AnaliseView

function static_build_analise_columns(
    handleEditClick: (id: GridRowId) => () => void,
    handleDeleteClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'tipo',
            headerName: 'Tipo',
            width: 100,
            valueFormatter: (value: string) => {
                switch (value) {
                    case 'CEDENTE_SACADO':
                        return 'Cedente/Sacado'
                    case 'CEDENTE':
                        return 'Cedente'
                    case 'SACADO':
                        return 'Sacado'
                    case 'TITULO':
                        return 'Título'
                    case 'NFE':
                        return 'Nfe'
                    case 'LOTE_CNPJ_CPF':
                        return 'Lote CNPJ/CPF'
                    default:
                        return ''
                }
            }
        },
        {
            field: 'nome',
            headerName: 'Nome',
            width: 300
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 100,
            valueFormatter: (value: string) => {
                return value === 'ATIVO' ? 'Ativo' : 'Inativo'
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                ]
            }
        }
    ]
}

function static_getAnaliseRowId(row: AnaliseRow) {
    return row.id + ''
}
